import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import {
  DEFAULT_ITEMS,
  DEFAULT_LISTINGS_PER_MONTH,
  calculateListingCost,
  formatCurrency,
} from "@/data/tools/listing-cost-defaults"
import type { ListingCostItem } from "@/data/tools/listing-cost-defaults"
import { CalculatorResultCard, CalculatorSlider, CalculatorActionsBar } from "@/components/public/calculators"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "How much does it cost to market a property listing?",
    a: "A typical property listing costs £500-1,000 to market when you include photography (£100-200), floorplans (£80-150), EPC (£60-120), brochure printing (£50-100), and video (£300-800 for a videographer). With QuiteRight, the video cost drops to as little as £2.45 per video on the Managed plan.",
  },
  {
    q: "How much do property videographers charge?",
    a: "Professional property videographers in the UK typically charge £300-800 per property visit, depending on the property size, location, and turnaround time. Premium videographers in London can charge £1,000+. With QuiteRight, you can create unlimited listing videos from £49/month, less than the cost of a single videographer visit.",
  },
  {
    q: "Should I pay for video on every listing?",
    a: "Yes, if you can afford to. Properties marketed with video receive 2x more enquiries on average. With QuiteRight's pricing, adding video to every listing costs pennies per property, and the return from faster sales and more viewings far outweighs the investment.",
  },
  {
    q: "What is included in a property listing presentation?",
    a: "A strong listing presentation typically includes: professional photography, measured floorplans, an EPC certificate, property particulars (brochure), a for-sale board, portal listing (Rightmove/Zoopla), and increasingly, a short-form video ad for social media. The best agents also include a social media marketing campaign.",
  },
  {
    q: "Can I reduce photography costs without losing quality?",
    a: "There are a few options. Some agents bring photography in-house with a decent DSLR and training, which reduces the per-listing cost significantly after the initial investment. Others negotiate bulk rates with photographers for a guaranteed number of shoots per month. What you should not cut corners on is image quality itself, as poor photos hurt your brand and reduce enquiries.",
  },
  {
    q: "Is a floorplan really necessary for every listing?",
    a: "Yes. Rightmove data shows that listings with floorplans receive 30% more engagement than those without. Buyers use floorplans to understand the layout before booking a viewing, which means the viewings you do get are more qualified. At £80-150 per property, floorplans are one of the best-value marketing investments you can make.",
  },
  {
    q: "How do I reduce the cost of an EPC?",
    a: "EPC costs are relatively fixed, typically £60-120 depending on property size and location. You can negotiate a small discount by using the same assessor regularly, but the main saving is ensuring you order the EPC early so you are not paying rush fees. Remember that EPCs are valid for 10 years, so re-sales within that window do not need a new one.",
  },
  {
    q: "Are printed brochures still worth the cost?",
    a: "Printed brochures add a premium feel that digital-only materials cannot match, particularly for higher-value properties. For standard listings, a well-designed PDF brochure sent by email is usually sufficient and costs nothing to distribute. Reserve printed copies for viewings and market appraisals where a physical takeaway makes a stronger impression.",
  },
  {
    q: "How do listing costs differ between sole and multi-agency?",
    a: "The marketing costs per listing are the same regardless of your agency agreement. However, multi-agency agreements typically come with higher commission rates (2-3% vs 1-1.5% for sole agency), which means the vendor is paying more overall. The marketing investment remains your responsibility either way.",
  },
  {
    q: "What is the most cost-effective way to add video to every listing?",
    a: "Use a platform like QuiteRight that creates videos from your existing listing photographs. At £49-299 per month depending on volume, you can add video to every listing for a fraction of the cost of a single videographer visit. The videos are ready in under 60 seconds, so there is no scheduling or waiting involved.",
  },
]

/* ------------------------------------------------------------------ */
/* Cost comparison table                                               */
/* ------------------------------------------------------------------ */

function CostComparisonTable({
  items,
}: {
  items: ListingCostItem[]
}) {
  return (
    <div className="rounded-xl border border-stone bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-charcoal">
        Cost per listing breakdown
      </p>
      <div className="mt-3 space-y-2">
        {/* Header */}
        <div className="grid grid-cols-[1fr_80px_80px] gap-2 text-[10px] font-medium uppercase tracking-wider text-warm-grey">
          <span>Item</span>
          <span className="text-right">Traditional</span>
          <span className="text-right">With QuiteRight</span>
        </div>
        {items.map((item) => {
          const isSaving = item.traditionalCost > item.withQuiteRight
          return (
            <div
              key={item.id}
              className="grid grid-cols-[1fr_80px_80px] items-center gap-2 text-sm"
            >
              <span className="text-slate">{item.name}</span>
              <span className="text-right font-medium text-charcoal">
                {formatCurrency(item.traditionalCost)}
              </span>
              <span
                className={`text-right font-medium ${
                  isSaving ? "text-red" : "text-charcoal"
                }`}
              >
                {formatCurrency(item.withQuiteRight)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function ListingCostCalculatorPage({
  meta,
}: {
  meta: SeoMeta
}) {
  const initialListingsPerMonth = useMemo(() => {
    if (typeof window === "undefined") return DEFAULT_LISTINGS_PER_MONTH
    const sp = new URLSearchParams(window.location.search)
    const raw = sp.get("listingsPerMonth")
    if (raw !== null) {
      const num = parseInt(raw, 10)
      if (!isNaN(num) && num > 0) return num
    }
    return DEFAULT_LISTINGS_PER_MONTH
  }, [])

  const [items, setItems] = useState<ListingCostItem[]>(
    DEFAULT_ITEMS.map((item) => ({ ...item })),
  )
  const [listingsPerMonth, setListingsPerMonth] = useState(initialListingsPerMonth)

  const updateItem = (index: number, field: "traditionalCost" | "withQuiteRight", value: number) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    )
  }

  const result = calculateListingCost(items, listingsPerMonth)

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("listingsPerMonth", String(listingsPerMonth))
    for (const item of items) {
      params.set(`${item.id}_traditional`, String(item.traditionalCost))
      params.set(`${item.id}_quiteright`, String(item.withQuiteRight))
    }
    window.history.replaceState(null, "", `?${params.toString()}`)
  }, [items, listingsPerMonth])

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?listingsPerMonth=${listingsPerMonth}`
      : ""
  const shareTitle = `Save ${formatCurrency(result.savingPerListing)} per listing with QuiteRight (${formatCurrency(result.traditionalTotal)} vs ${formatCurrency(result.quiterightTotal)})`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Listing Cost Comparison",
      subtitle: `Traditional videographer vs QuiteRight, ${listingsPerMonth} listings/month`,
      sections: [
        {
          title: "Summary",
          rows: [
            { label: "Saving per listing", value: formatCurrency(result.savingPerListing), sub: `${formatCurrency(result.traditionalTotal)} vs ${formatCurrency(result.quiterightTotal)} per listing`, accent: true },
            { label: "Annual traditional cost", value: formatCurrency(result.annualTraditionalCost), sub: `${listingsPerMonth * 12} listings/year` },
            { label: "Annual with QuiteRight", value: formatCurrency(result.annualQuiterightCost), sub: `${listingsPerMonth * 12} listings/year` },
            { label: "Annual saving", value: formatCurrency(result.annualSaving), accent: true },
          ],
        },
        {
          title: "Cost Breakdown per Listing",
          rows: items.map((item) => ({
            label: item.name,
            value: `${formatCurrency(item.traditionalCost)} vs ${formatCurrency(item.withQuiteRight)}`,
            sub: item.traditionalCost > item.withQuiteRight ? `Save ${formatCurrency(item.traditionalCost - item.withQuiteRight)}` : undefined,
          })),
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Listing Cost Calculator",
            description: meta.description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Listing Cost Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Listing cost calculator
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Compare the cost of marketing a property listing with a traditional
            videographer versus QuiteRight. See how much you could save per
            listing and across your annual portfolio.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-6">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Cost per listing<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Adjust the costs to match your agency.
                </p>
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-[1fr_80px_80px] items-end gap-3">
                <span className="text-xs font-medium uppercase tracking-wider text-warm-grey">
                  Item
                </span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-warm-grey">
                  Traditional
                </span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-warm-grey">
                  QuiteRight
                </span>
              </div>

              <div className="space-y-3">
                {items.map((item, index) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr_80px_80px] items-center gap-3"
                  >
                    <div>
                      <span className="text-sm font-medium text-charcoal">
                        {item.name}
                      </span>
                      <p className="text-[10px] text-warm-grey">
                        {item.description}
                      </p>
                    </div>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-warm-grey">
                        £
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={item.traditionalCost}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "")
                          const num = parseInt(raw, 10)
                          if (!isNaN(num)) updateItem(index, "traditionalCost", num)
                          if (raw === "") updateItem(index, "traditionalCost", 0)
                        }}
                        className="w-full rounded-lg border border-stone bg-white py-2 pl-5 pr-1 text-right text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-1 focus:ring-red/20"
                      />
                    </div>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-warm-grey">
                        £
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={item.withQuiteRight}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^0-9]/g, "")
                          const num = parseInt(raw, 10)
                          if (!isNaN(num)) updateItem(index, "withQuiteRight", num)
                          if (raw === "") updateItem(index, "withQuiteRight", 0)
                        }}
                        className="w-full rounded-lg border border-stone bg-white py-2 pl-5 pr-1 text-right text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-1 focus:ring-red/20"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-stone pt-6">
                <CalculatorSlider
                  label="Listings per month"
                  value={listingsPerMonth}
                  min={1}
                  max={30}
                  step={1}
                  formatValue={(v) => `${v}`}
                  onChange={setListingsPerMonth}
                />
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your savings<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  How much you save per listing and annually.
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              <CalculatorResultCard
                label="Saving per listing"
                value={formatCurrency(result.savingPerListing)}
                sub={`${formatCurrency(result.traditionalTotal)} → ${formatCurrency(result.quiterightTotal)} per listing`}
                accent
                large
              />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Traditional cost/listing"
                  value={formatCurrency(result.traditionalTotal)}
                  sub="With videographer"
                />
                <CalculatorResultCard
                  label="QuiteRight cost/listing"
                  value={formatCurrency(result.quiterightTotal)}
                  sub="Same quality, less cost"
                />
                <CalculatorResultCard
                  label="Annual traditional cost"
                  value={formatCurrency(result.annualTraditionalCost)}
                  sub={`${listingsPerMonth * 12} listings/year`}
                />
                <CalculatorResultCard
                  label="Annual with QuiteRight"
                  value={formatCurrency(result.annualQuiterightCost)}
                  sub={`${listingsPerMonth * 12} listings/year`}
                />
              </div>

              {/* Annual saving hero */}
              <div className="rounded-xl border border-red/20 bg-red/[0.03] p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-red">
                  Annual saving with QuiteRight
                </p>
                <p className="mt-2 font-display text-3xl font-bold text-red md:text-4xl">
                  {formatCurrency(result.annualSaving)}
                </p>
                <p className="mt-1 text-sm text-slate">
                  That&rsquo;s {formatCurrency(result.savingPerListing)} saved
                  on every listing, {listingsPerMonth * 12} times a year.
                </p>
              </div>

              <CostComparisonTable items={result.items} />
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How listing costs add up<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Every new listing requires investment: photography, floorplans,
              EPC certification, printed particulars, a for-sale board, and
              increasingly, video content. With a traditional videographer,
              video is the single most expensive line item, often £300-800
              per property.
            </p>
            <p>
              QuiteRight replaces the videographer with an automated platform
              that creates professional listing videos from your existing
              photographs. On the Managed plan (£299/month for 50 videos),
              that works out to about £6 per video, roughly 1-2% of what
              you&rsquo;d pay a videographer.
            </p>
            <p>
              The default costs shown are averages for UK estate agents. Adjust
              them to match your actual costs for an accurate comparison.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "98%", label: "lower video cost with QuiteRight vs videographer" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "<60s", label: "to create a listing video with QuiteRight" },
            { stat: "£6", label: "average cost per video on the Managed plan" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related tools */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              href="/tools/marketing-roi-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Marketing ROI calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Compare return on investment across all your marketing channels.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            50 videos for the price of one videographer
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Stop paying hundreds per listing for video. QuiteRight gives you
            professional property videos for a fraction of the cost, and they&rsquo;re
            ready in seconds, not days.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/why-quiteright"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Why QuiteRight
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
